import React, { useEffect, useState } from 'react';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { PageTitle } from '../../layout/core';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { GoBackBtn } from '../header/GoBackBtn';
import { fetchAgencyList } from '../../store/merchnts/actions/agents';
import { TableComponent } from '../TableComponent';
import { SalesAgentsList } from './SalesAgentsList';
import { AgencyTableRow } from './AgencyTableRow';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { AddNewAgency } from './AddNewAgency';
import { salesAgenciesBreadCrumbs } from '../../views/agencies/CommissionsPage';
import { ROUTES } from '../../routing/routesEnums';
import { useLocation } from 'react-router';

const AGENCY_COLUMNS = [
	{
		key: 'name',
		title: 'Name',
		isSalesAgencyPage: true
		// additionalColumnClassName: 'w-200px',
	},
	{
		key: 'id',
		title: 'Id',
		isSalesAgencyPage: false,
		additionalColumnClassName: 'id-width',
	},
	{
		key: 'actions',
		title: 'PARTNERS.ACTIONS',
		isSalesAgencyPage: true,
		additionalColumnClassName: 'w-50px',
	}
];

export const AgencyDetails = (): React.ReactElement => {
	const { merchantState: { agenciesList, isAgenciesListLoading }, dispatch } = useMerchants();

	const { t } = useTranslation();
	const { id } = useParams();
	const { state } = useLocation();

	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	useEffect(() => {
		void fetchAgencyList(dispatch);
	}, []);

	const tableData = agenciesList?.filter(el => el?.id === id);

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

	const closeAgencyModal = (): void => setIsModalOpen(false);

	const handleSuccess = (): void => {
		setIsModalOpen(false);
		setModalState({
			isOpen: true,
			modalTitle: t('AGENCY.MODAL_TITLE_EDIT'),
			content: (
				<div className="fs-6">
					{t('AGENCY.SUCCESS_MODAL_TEXT')}
				</div>
			),
			confirmButtonText: t('AGENCY.SUCCESS_BTN_NAME'),
			minHeight: 100,
			onConfirm: closeModal,
			onClose: closeModal
		});
	};

	const handleError = (): void => {
		setIsModalOpen(false);
		setModalState({
			isOpen: true,
			modalTitle: t('AGENCY.MODAL_TITLE_EDIT'),
			content: (
				<div className="fs-6">
					{t('AGENCY.FAIL_MODAL_TEXT')}
				</div>
			),
			confirmButtonText: t('AGENCY.FAIL_BTN_NAME'),
			minHeight: 100,
			onConfirm: () => {
				closeModal();
				setIsModalOpen(true);
			},
			onClose: closeModal
		});
	};

	return (
		<>
			<PageTitle breadcrumbs={salesAgenciesBreadCrumbs}>{id}</PageTitle>
			<div className="mb-5">
				<GoBackBtn path={`${ROUTES.AGENCIES}${ROUTES.AGENCIES}${state?.search ?? ''}`} />
			</div>
			<div className="card mb-5">
				<TableComponent
					column={AGENCY_COLUMNS}
					title={t('AGENCY.AGENCY_DETAILS')}
					isDataLoading={isAgenciesListLoading}
					tableData={tableData}
					tableMinHeight={tableData.length ? '' : 'table-min-height'}
					getRowComponent={(agent): JSX.Element => (
						<AgencyTableRow
							{...agent}
							key={agent?.id}
							onEdit={(): void => setIsModalOpen(true)}
						/>
					)}
				/>
			</div>

			<SalesAgentsList/>
			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>
			<ModalWindow
				isOpen={isModalOpen}
				backdrop="static"
				onClose={closeAgencyModal}
				modalTitle={t('AGENCY.MODAL_TITLE_EDIT')}
				minHeight={150}
				content={
					<AddNewAgency
						agency={tableData[0]}
						onCancel={closeAgencyModal}
						onSuccess={handleSuccess}
						onError={handleError}
					/>
				}
			/>
		</>
	);
};
